<div class="container mx-auto p-6 max-w-7xl">
  <!-- Header Section -->
  <div class="flex items-center gap-4 mb-8 p-6 bg-gradient-to-r from-primary/10 to-secondary/10 rounded-xl border border-primary/20">
    <app-icon name="school" size="lg" class="text-primary"></app-icon>
    <h1 class="text-3xl font-bold text-primary" data-testid="course-list-heading">My Courses</h1>
  </div>

  <!-- Organization Context Indicator (shown when user belongs to multiple orgs) -->
  @if (data.availableOrganizations.length > 1 && !isLoading) {
    <div class="alert alert-info mb-6">
      <div class="flex items-center gap-2 flex-1">
        <app-icon name="corporate_fare" size="sm"></app-icon>
        <span>Viewing courses from: <strong class="user-text-wrap" [title]="getCurrentOrgName()">{{ getCurrentOrgName() }}</strong></span>
      </div>
      <span class="text-sm opacity-70">Use the org switcher to change organizations</span>
    </div>
  }

  <!-- Loading State -->
  @if (isLoading) {
    <div class="flex justify-center items-center py-16" role="status" aria-live="polite" data-testid="course-list-loading">
      <span class="loading loading-spinner loading-lg"></span>
      <span class="ml-3 text-lg">Loading your courses...</span>
    </div>
  }

  <!-- Error Message -->
  @if (errorMessage) {
    <div class="alert alert-error mb-6" role="alert" data-testid="course-list-error-alert">
      <app-icon name="warning" size="sm"></app-icon>
      <span>{{ errorMessage }}</span>
    </div>
  }

  <!-- Tabs (only show if user has both roles) -->
  @if (!isLoading && hasInstructorRole && courses.length > 0) {
    <!-- KAN-617: large tabs from sm up only; below it the compact size and
         nowrap keep each label and its icon on one line (see the .scss). -->
    <div role="tablist" class="my-courses-tabs tabs tabs-bordered sm:tabs-lg mb-8">
      <button
        role="tab"
        class="tab flex-nowrap whitespace-nowrap"
        [class.tab-active]="activeTab === 'teaching'"
        (click)="activeTab = 'teaching'"
        data-testid="tab-teaching">
        <app-icon name="edit_note" size="sm" class="mr-2"></app-icon>
        Courses I Teach
      </button>
      <button
        role="tab"
        class="tab flex-nowrap whitespace-nowrap"
        [class.tab-active]="activeTab === 'learning'"
        (click)="activeTab = 'learning'"
        data-testid="tab-learning">
        <app-icon name="school" size="sm" class="mr-2"></app-icon>
        My Learning
      </button>
    </div>
  }

  <!-- ===== Instructor Courses (Tab or Standalone) ===== -->
  @if (hasInstructorRole && (activeTab === 'teaching' || courses.length === 0)) {
    <div>
      <div class="flex justify-end mb-4">
        <label class="label cursor-pointer gap-2">
          <input type="checkbox"
            class="checkbox checkbox-sm"
            [checked]="showArchivedTeachingCourses"
            (change)="onShowArchivedChange($any($event.target).checked)"
            data-testid="teaching-show-archived">
          <span class="label-text text-sm">Show Archived</span>
        </label>
      </div>
      <!-- Loading state for instructor courses -->
      @if (isLoadingInstructorCourses) {
        <div class="text-center py-8">
          <span class="loading loading-spinner loading-md"></span>
          <p class="text-sm mt-2">Loading instructor courses...</p>
        </div>
      }

      @if (!isLoadingInstructorCourses && allInstructorCourses.length > 0) {
        <!-- Filter Bar -->
        <div class="mb-6">
          <app-course-filter-bar
            [searchTerm]="data.getOrgFilter('instructor-global').searchTerm"
            [sortField]="data.getOrgFilter('instructor-global').sortField"
            [sortOrder]="data.getOrgFilter('instructor-global').sortOrder"
            [sortOptions]="instructorSortOptions"
            [showSearch]="true"
            [resultCount]="filteredInstructorCourses.length"
            [totalCount]="allInstructorCourses.length"
            (searchTermChange)="onInstructorSearchChange($event)"
            (sortChange)="onInstructorSortChange($event)">
          </app-course-filter-bar>
        </div>

        <!-- Create Course Button -->
        <div class="mb-6">
          <button type="button"
            (click)="navigateToCreateCourse(selectedOrgFilter)"
            class="btn btn-primary btn-sm"
            data-testid="course-list-create-btn">
            <app-icon name="add_circle" size="xs" class="mr-1"></app-icon>
            Create New Course
          </button>
        </div>

        <!-- Card Grid -->
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
          @for (course of filteredInstructorCourses; track course.id) {
            <app-course-card
              mode="instructor"
              [courseId]="course.id"
              [title]="course.name || course.title || 'Untitled'"
              [description]="course.description || ''"
              [thumbnail]="course.thumbnail || null"
              [status]="course.status || 'draft'"
              [modulesCount]="course.modulesCount || 0"
              [createdAt]="course.createdAt || ''"
              [organizationName]="course.organizationName || 'Unknown Org'"
              (cardClick)="onInstructorCourseClick($event)">
            </app-course-card>
          }
        </div>

        <!-- No search results -->
        @if (filteredInstructorCourses.length === 0) {
          <div class="text-center py-12 text-base-content opacity-50">
            <app-icon name="search_off" size="lg" class="mx-auto mb-2"></app-icon>
            <p>No courses match your search</p>
          </div>
        }
      }

      <!-- Empty state when instructor has no courses -->
      @if (!isLoadingInstructorCourses && allInstructorCourses.length === 0) {
        <div class="text-center py-8">
          <div class="card bg-base-100 shadow-md max-w-md mx-auto">
            <div class="card-body items-center text-center">
              <app-icon name="school" size="lg" class="text-primary/40 mb-2"></app-icon>
              <h3 class="card-title text-lg">{{ showArchivedTeachingCourses ? 'No Courses Yet' : 'No Active Courses' }}</h3>
              @if (showArchivedTeachingCourses) {
                <p class="text-base-content/70">You haven't created any courses yet. Start creating your first course!</p>
              } @else {
                <p class="text-base-content/70">Turn on Show Archived to find archived courses, or create a new course.</p>
              }
              <div class="card-actions justify-center mt-4">
                <button type="button" (click)="navigateToCreateCourse(selectedOrgFilter)" class="btn btn-primary btn-sm"
                  data-testid="course-list-create-btn">
                  <app-icon name="add_circle" size="xs" class="mr-1"></app-icon>
                  Create Course
                </button>
              </div>
            </div>
          </div>
        </div>
      }
    </div>
  }

  <!-- ===== Learner-only Empty State (no instructor role, no courses) ===== -->
  @if (!isLoading && !courses.length && !hasInstructorRole) {
    <div class="text-center py-16" data-testid="course-list-empty">
      <div class="text-6xl mb-6">
        <app-icon name="book" size="xl" class="mx-auto text-base-content/30"></app-icon>
      </div>
      <h2 class="text-2xl font-bold mb-4">No Courses Yet</h2>
      <p class="text-lg mb-4 text-base-content opacity-70">You don't have any courses assigned yet.</p>
      <p class="text-base-content opacity-50">Contact your organization's instructor to get access to courses.</p>
    </div>
  }

  <!-- ===== My Learning (Tab or Standalone) ===== -->
  @if (!isLoading && courses.length > 0 && (activeTab === 'learning' || !hasInstructorRole)) {
    <div>
      <!-- Checkout error (KAN-353 renew access) -->
      @if (checkoutError) {
        <div class="alert alert-error mb-4" data-testid="checkout-error-alert">
          <app-icon name="error" size="sm"></app-icon>
          <span>{{ checkoutError }}</span>
        </div>
      }
      <!-- Filter Bar -->
      <div class="mb-6">
        <app-course-filter-bar
          [searchTerm]="data.getOrgFilter('learner-global').searchTerm"
          [sortField]="data.getOrgFilter('learner-global').sortField"
          [sortOrder]="data.getOrgFilter('learner-global').sortOrder"
          [sortOptions]="learnerSortOptions"
          [showSearch]="true"
          [resultCount]="filteredLearnerCourses.length"
          [totalCount]="courses.length"
          (searchTermChange)="onLearnerSearchChange($event)"
          (sortChange)="onLearnerSortChange($event)">
        </app-course-filter-bar>
      </div>

      <!-- Card Grid -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        @for (course of filteredLearnerCourses; track course.id) {
          <app-course-card
            mode="learner"
            [courseId]="course.id"
            [title]="course.title || course.name || 'Untitled Course'"
            [description]="course.description || ''"
            [thumbnail]="course.thumbnail || null"
            [learnerStatus]="getCourseStatus(course)"
            [progressPercent]="getProgressPercentage(course)"
            [hasCertificate]="!!getCertificateForCourse(course.id)"
            [canContinue]="shouldShowContinueButton(course)"
            [enrollmentDeadline]="course.enrollmentDeadline || ''"
            [unitsCount]="course.unitsCount || 0"
            [modulesCount]="course.modulesCount || 0"
            [quizzesCount]="course.quizzesCount || 0"
            [organizationName]="course.organizationName || 'Unknown Org'"
            [accessExpired]="!!course.accessExpired"
            (cardClick)="onLearnerCourseClick($event)"
            (continueClick)="onContinueCourse($event)"
            (renewClick)="onRenewAccess($event)">
          </app-course-card>
        }
      </div>

      <!-- No search results -->
      @if (filteredLearnerCourses.length === 0 && courses.length > 0) {
        <div class="text-center py-12 text-base-content opacity-50">
          <app-icon name="search_off" size="lg" class="mx-auto mb-2"></app-icon>
          <p>No courses match your search</p>
        </div>
      }
    </div>
  }

  <!-- ===== Certificates Section ===== -->
  @if (certificates.length) {
    <div class="mt-12">
      <div class="divider">
        <span class="text-xl font-semibold flex items-center gap-2 text-warning">
          <app-icon name="military_tech" size="md" class="text-warning"></app-icon>
          My Certificates
        </span>
      </div>
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
        @for (cert of certificates; track cert) {
          <div class="card bg-warning/10 border border-warning/30 shadow-md" data-testid="certificate-card">
            <div class="card-body">
              <div class="flex items-center gap-3 mb-3">
                <app-icon name="military_tech" size="md" class="text-base-content flex-shrink-0"></app-icon>
                <h3 class="card-title text-lg truncate min-w-0 flex-1" [title]="cert.courseName">{{ cert.courseName }}</h3>
              </div>
              <p class="text-sm text-base-content mb-4">
                <app-icon name="calendar_today" size="sm" class="inline mr-1"></app-icon>
                Issued on {{ cert.issuedAt | date:'mediumDate' }}
              </p>
              <div class="card-actions">
                <a [routerLink]="['/certificates', cert.id]" class="btn btn-sm btn-warning" data-testid="view-certificate-btn">
                  <app-icon name="open_in_new" size="sm"></app-icon>
                  View Certificate
                </a>
              </div>
            </div>
          </div>
        }
      </div>
    </div>
  }

  <!-- Back to Dashboard -->
  <div class="flex justify-center mt-12">
    <a routerLink="/dashboard" class="btn btn-outline" data-testid="course-list-back-btn">
      <app-icon name="arrow_back" size="sm"></app-icon>
      Back to Dashboard
    </a>
  </div>
</div>
